import {
  ago,
  type Config,
  fmtDateTime,
  isOverdue,
  type PeopleMap,
  stageOf,
  type TicketSummary,
} from '@hrms/shared'
import { LuCalendarClock, LuInbox, LuMessageSquare, LuPlus, LuRefreshCw } from 'react-icons/lu'
import { useCurrentUser } from '../../../auth/AuthProvider.tsx'
import { Button, EmptyState } from '../../../components/ui/index.ts'
import { cn } from '../../../lib/cn.ts'
import { useConfig } from '../../../lib/config.ts'
import { hint } from '../../../lib/hint.ts'
import { useNow } from '../../../lib/useNow.ts'
import { useMyTickets } from '../tickets/api.ts'
import { openRaiseTicket } from '../tickets/raiseStore.ts'
import { CategoryChip, EtaText, LockIcon, personName, StatusLozenge } from '../tickets/ui.tsx'
import { useOpenTicket } from '../tickets/useTicketParam.ts'
import { PageHeader } from './PageHeader.tsx'

function StatusPhrase({
  ticket,
  people,
  config,
}: {
  ticket: TicketSummary
  people: PeopleMap
  config: Config
}) {
  if (stageOf(config.statuses, ticket.statusId) === 'waiting')
    return <span className="font-medium text-amber-fg">HR is waiting for your reply</span>
  if (ticket.assignee) return <span>Handled by {personName(people, ticket.assignee)}</span>
  return <span>Waiting for HR to pick this up</span>
}

function TicketRow({
  ticket,
  people,
  config,
  now,
  onOpen,
}: {
  ticket: TicketSummary
  people: PeopleMap
  config: Config
  now: number
  onOpen: () => void
}) {
  const stage = stageOf(config.statuses, ticket.statusId)
  const showEta = stage !== 'done' && stage !== 'waiting'
  const dot = (
    <span aria-hidden className="text-faint">
      ·
    </span>
  )
  return (
    <li>
      <button
        type="button"
        onClick={onOpen}
        data-tour="my-ticket-row"
        aria-label={`${ticket.key}: ${ticket.title}${ticket.confidential ? ' (confidential)' : ''}`}
        className="grid w-full grid-cols-[1fr_auto] items-start gap-x-3 gap-y-1 rounded-panel border border-border bg-surface px-4 py-3.5 text-left transition-colors hover:border-border-strong hover:bg-surface-2/40"
      >
        <span className="flex min-w-0 items-center gap-1.5 text-[15px] font-semibold">
          <span className="min-w-0 break-words">{ticket.title}</span>
          {ticket.confidential && <LockIcon employee />}
        </span>
        <StatusLozenge statusId={ticket.statusId} statuses={config.statuses} />
        <span className="col-span-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px] text-muted">
          <span className="tabular font-medium text-text">{ticket.key}</span>
          <CategoryChip categoryId={ticket.categoryId} categories={config.categories} />
          <StatusPhrase ticket={ticket} people={people} config={config} />
          {showEta && (
            <>
              {dot}
              <span className="inline-flex items-center gap-1">
                <LuCalendarClock aria-hidden className="size-3.5 text-blue-fg" />
                {!isOverdue(ticket, now, config.statuses) && 'Expected by '}
                <EtaText ticket={ticket} config={config} viewer="employee" now={now} />
              </span>
            </>
          )}
          {dot}
          <span className="inline-flex items-center gap-1">
            <LuMessageSquare aria-hidden className="size-3.5 text-violet-fg" />
            {ticket.commentCount} {ticket.commentCount === 1 ? 'comment' : 'comments'}
          </span>
          {dot}
          <span {...hint(fmtDateTime(ticket.updatedAt, { now }))}>
            Updated {ago(ticket.updatedAt, { now })}
          </span>
        </span>
      </button>
    </li>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="mt-[22px] mb-2 text-[13px] font-semibold text-muted">{title}</h2>
      <ul className="flex flex-col gap-2">{children}</ul>
    </section>
  )
}

/** Employee: tickets I raised, Open and Resolved (DESIGN.md §3.4). */
export function MyTicketsPage() {
  const user = useCurrentUser()
  const mine = useMyTickets()
  const { data: config } = useConfig()
  const now = useNow()
  const openTicket = useOpenTicket()
  const firstName = user.name.split(' ')[0] || user.name

  const raiseButton = (label = true) => (
    <Button
      variant="primary"
      onClick={() => openRaiseTicket()}
      aria-label="Raise a ticket"
      {...hint('Ask HR for help with anything')}
    >
      <LuPlus aria-hidden />
      <span className={cn(label && 'max-[720px]:sr-only')}>Raise a ticket</span>
    </Button>
  )

  let content: React.ReactNode
  if (mine.isError)
    content = (
      <EmptyState
        icon={LuRefreshCw}
        color="amber"
        title="Couldn’t load your tickets"
        action={<Button onClick={() => void mine.refetch()}>Try again</Button>}
      >
        Check your connection and try again.
      </EmptyState>
    )
  else if (!mine.data || !config) content = <p className="text-muted">Loading your tickets…</p>
  else if (mine.data.tickets.length === 0)
    content = (
      <EmptyState
        icon={LuInbox}
        color="blue"
        title="You haven't raised any tickets yet"
        action={raiseButton(false)}
      >
        Questions about leave, reimbursements, payroll or the office all go here.
      </EmptyState>
    )
  else {
    const { tickets, people } = mine.data
    const done = (t: TicketSummary) => stageOf(config.statuses, t.statusId) === 'done'
    const open = tickets.filter((t) => !done(t))
    const resolved = tickets.filter(done)
    const row = (t: TicketSummary) => (
      <TicketRow
        key={t.id}
        ticket={t}
        people={people}
        config={config}
        now={now}
        onOpen={() => openTicket(t.key)}
      />
    )
    content = (
      <>
        {open.length > 0 && <Section title={`Open (${open.length})`}>{open.map(row)}</Section>}
        {resolved.length > 0 && (
          <Section title={`Resolved (${resolved.length})`}>{resolved.map(row)}</Section>
        )}
      </>
    )
  }

  return (
    <div className="mx-auto max-w-[880px]">
      <PageHeader
        title="My tickets"
        description={`Hi ${firstName}. Raise an issue with HR and follow its progress here.`}
        actions={raiseButton()}
      />
      {content}
    </div>
  )
}
